import { SPINNER_CLASSNAME } from './constants';
import getSharingInformation from '../actions/getSharingInformation';
import { onSharePointLinkDetected } from '../actions/internalActions';
import datapoints from '../datapoints';
import LinkPlugin from '../plugin/LinkPlugin';
import { logUsage } from 'owa-analytics';
import { AttachmentWellViewState } from 'owa-attachment-well-data';
import { createSharingLinkView, displaySpinnerOverlay } from 'owa-compose-link';
import { addBeautifiedPrefixToId, addComposeLinkViewState } from 'owa-compose-link';
import formatShortMonthDayYear from 'owa-datetime/lib/formatters/formatShortMonthDayYear';
import isBefore from 'owa-datetime/lib/owaDate/isBefore';
import { userDate } from 'owa-datetime/lib/userDate';
import now from 'owa-datetime/lib/userDate/now';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FileIcon, getIconForFile } from 'owa-file-icon';
import {
    getAttachmentPermissionLevelFromSharingLinkKind,
    getSharingLink,
    SharingLinkInfo,
} from 'owa-link-data';
import AttachmentPermissionLevel from 'owa-service/lib/contract/AttachmentPermissionLevel';
import getPackageBaseUrl from 'owa-session-store/lib/actions/getPackageBaseUrl';
import { isGuestUrlPermissionLevel, isValidGetSharingInfoResponse } from 'owa-sharing-data';
import { format, isStringNullOrWhiteSpace } from 'owa-strings';
import strings from 'owa-strings/owa-mail-strings';
import { trace } from 'owa-trace';
import { PositionType } from 'roosterjs-editor-types';
import {
    COMPOSE_ONLY_ATTACHMENT_CONTENT_END,
    COMPOSE_ONLY_ATTACHMENT_CONTENT_START,
} from 'owa-attachment-link-common';
import { tryCheckRecipientPermissionForOneLink } from 'owa-recipient-permission-checker';

const classNames = require('classnames/bind');

export default async function getSharingInfoToTryUpdateLink(
    linkPlugin: LinkPlugin,
    shouldBeautifyLink: boolean, // We would display a spinner during server call, change the text, add file icon, display error icon & red text for links with sharing issues, context menu on click
    shouldRehydrate: boolean, // We won't change the link, but display error icon & red text for links with sharing issues, context menu on click
    isPreviouslyBeautified: boolean,
    anchorElement: HTMLAnchorElement,
    viewState: AttachmentWellViewState,
    linkIndex: number, // The link index when there are a collection of anchor elements; otherwise, just put 0
    linkIdsWithSpinner: string[],
    shouldTryShowContextMenuOnMount: boolean = false
): Promise<void> {
    // Todo: VSTO 39277: refactor getSharingInfoToTryUpdateLink and its parameters
    trace.info('LinkPlugin.getSharingInfoToTryUpdateLink');
    let addedElements: Node[] | null;
    if (shouldBeautifyLink) {
        // Spinner is only needed when we will beautify the link
        linkPlugin.editor.addUndoSnapshot();
        linkIdsWithSpinner.push(anchorElement.id);
        addedElements = displaySpinner(anchorElement);
        linkPlugin.editor.select(anchorElement, PositionType.After);
        linkPlugin.editor.triggerContentChangedEvent('Link');
        logUsage(datapoints.AttachmentComposeSPLinksShouldReplaceText.datapoint.name);
    }

    const linkId = anchorElement.id;
    let sharingLink: SharingLinkInfo = getSharingLink(anchorElement.id);
    let response;

    // After user leaves the compose and comes back, the plugin has been disposed and we need to rerender the UI
    // But we don't need to perform the following tasks
    // 1. send the server call for links we already have the getSharingInformation response in the model store(sharingLink will not be null)
    // 2. we never pass true for shouldBeautifyLink, so we don't need to remove Spinner nor add prefix to id.
    if (!sharingLink) {
        logUsage(datapoints.AttachmentComposeSPLinks.datapoint.name);
        response = await getSharingInformation(anchorElement.href);
        if (shouldBeautifyLink) {
            const index = linkIdsWithSpinner.indexOf(anchorElement.id);
            linkIdsWithSpinner.splice(index, 1);
            removeSpinner(anchorElement, addedElements);
        }

        // The following check is only needed after async call.
        if (!linkPlugin.editor) {
            // This plugin has been disposed. No need to do anything
            const {
                name: eventName,
                customData: customData,
            } = datapoints.ComposeEditorIsNullDuringProcessSPLinks.datapoint;
            logUsage(eventName, customData(linkIndex < 20 ? linkIndex : 20));
            return;
        }

        // If linkId changes during making server call
        // user has probably changed the link. No need to continue
        if (!areIdsFromSameLink(linkId, anchorElement.id)) {
            trace.warn(
                `LinkId changes during server call LinkIdBefore: ${linkId}, LinkIdNow: ${
                    anchorElement.id
                }`
            );
            return;
        }

        // This is the scenario when user converts the anchor element to text, no need to continue
        const linksMatchingId = linkPlugin.editor.queryElements('#' + anchorElement.id);
        if (linksMatchingId.length !== 1 || linksMatchingId[0] !== anchorElement) {
            trace.warn(
                `The result from querying the editor is not matching the original anchor element with id: ${
                    anchorElement.id
                }`
            );

            return;
        }

        // Undo snapshot must be created before the id is prefixed with the special beautified token
        linkPlugin.editor.addUndoSnapshot();
        const isValidResponse: boolean = isValidGetSharingInfoResponse(response);
        const hasValidFileName: boolean = !isStringNullOrWhiteSpace(response.FileName);

        if (shouldBeautifyLink && isValidResponse && hasValidFileName) {
            addBeautifiedPrefixToId(anchorElement);
        }

        if (isValidResponse && !hasValidFileName) {
            logUsage(datapoints.AttachmentComposeSPLinksResponseHasInvalidFileName.datapoint.name);
        }
    } else if (shouldBeautifyLink) {
        trace.error(
            'We should not pass true for shouldBeautifyLink when processing the same link again.'
        );
    }

    // If sharingLink already exists, we don't need to add it to model store nor update the sharing tips
    // we just need to create ComposeLinkViewState.
    if (!sharingLink) {
        onSharePointLinkDetected(
            anchorElement.id,
            anchorElement.href,
            viewState.sharingLinkIds,
            response,
            shouldBeautifyLink || shouldRehydrate,
            isPreviouslyBeautified
        );
    } else if (isPreviouslyBeautified || sharingLink.sharingInfo.getSharingInfoSucceeded) {
        addComposeLinkViewState(anchorElement.id);
    }

    // Always try to check recipient permission
    tryCheckRecipientPermissionForOneLink(
        anchorElement.id,
        linkPlugin.recipientWells,
        linkPlugin.fromAddress
    );

    // No other UI changes, just leave
    if (!shouldBeautifyLink && !shouldRehydrate) {
        return;
    }

    // Retrieve sharingLink if we haven't got one.
    if (!sharingLink) {
        sharingLink = getSharingLink(anchorElement.id);
    }

    const isLinkWithValidFileName: boolean =
        sharingLink && !isStringNullOrWhiteSpace(sharingLink.fileName);
    const isValidSharingInfo: boolean =
        sharingLink && sharingLink.sharingInfo.getSharingInfoSucceeded;

    // Beautify or rehydrate the link when conditions are met.
    if (isFeatureEnabled('doc-beautify-link') && (isValidSharingInfo || isPreviouslyBeautified)) {
        createSharingLinkView(
            linkPlugin.editor,
            linkPlugin.recipientWells,
            linkPlugin.fromAddress,
            viewState.composeId,
            anchorElement,
            sharingLink,
            isLinkWithValidFileName,
            sharingLink.mimeType,
            shouldBeautifyLink,
            isPreviouslyBeautified,
            shouldTryShowContextMenuOnMount
        );
    } else {
        // We don't rehydrate for WW.
        if (shouldBeautifyLink) {
            if (isLinkWithValidFileName) {
                displayFileIconAndName(
                    anchorElement,
                    sharingLink.fileName,
                    sharingLink.mimeType,
                    response.ExpirationDate
                );
            }

            if (isValidSharingInfo) {
                highlightBackground(anchorElement);
                const permissionLevel: AttachmentPermissionLevel = getAttachmentPermissionLevelFromSharingLinkKind(
                    response.SharingLinkKind
                );
                if (!response.CanShareInternally && !isGuestUrlPermissionLevel(permissionLevel)) {
                    displayErrorIcon(anchorElement);
                }
            }
        }
    }
}

const linkStyles = require('owa-compose-link/lib/components/Link.scss');
function displaySpinner(anchorElement: HTMLAnchorElement): Node[] {
    const newElements: Node[] = [];
    let spinnerContainer: HTMLElement;
    if (isFeatureEnabled('doc-beautify-link')) {
        anchorElement.setAttribute('contenteditable', 'false');
        anchorElement.style.pointerEvents = 'none';
        anchorElement.addEventListener('click', (event: MouseEvent) => {
            event.preventDefault();
        });
        spinnerContainer = document.createElement('span');
        spinnerContainer.className = SPINNER_CLASSNAME;
    } else {
        const spinnerImageContainer: HTMLImageElement = document.createElement('img');
        spinnerImageContainer.src = `${getPackageBaseUrl()}resources/images/spinner_16.gif`;
        spinnerImageContainer.className = linkStyles.linkSpinner;
        spinnerContainer = spinnerImageContainer;
    }

    newElements.push(document.createComment(COMPOSE_ONLY_ATTACHMENT_CONTENT_START));
    newElements.push(spinnerContainer);
    newElements.push(document.createComment(COMPOSE_ONLY_ATTACHMENT_CONTENT_END));
    newElements.forEach(newElement => {
        anchorElement.appendChild(newElement);
    });

    if (isFeatureEnabled('doc-beautify-link')) {
        displaySpinnerOverlay(spinnerContainer);
    }
    return newElements;
}

function removeSpinner(anchorElement: HTMLAnchorElement, addedElements: Node[]) {
    addedElements.forEach(element => {
        try {
            anchorElement.removeChild(element);
        } catch (Exception) {
            // In case user has removed the spinner
        }
    });

    if (isFeatureEnabled('doc-beautify-link')) {
        anchorElement.setAttribute('contenteditable', 'true');
        anchorElement.style.pointerEvents = 'auto';
        anchorElement.removeEventListener('click', (event: MouseEvent) => {
            event.preventDefault();
        });
    }
}

function displayFileIconAndName(
    anchorElement: HTMLAnchorElement,
    fileName: string,
    mimeType: string | null,
    expirationDate: string | null
) {
    anchorElement.text = fileName;

    if (!isFeatureEnabled('doc-beautify-link')) {
        return;
    }

    if (expirationDate) {
        const date = userDate(expirationDate);
        if (isBefore(now(), date)) {
            anchorElement.text = format(
                strings.expiringLinkText,
                anchorElement.text,
                formatShortMonthDayYear(date)
            );
        } else {
            anchorElement.className = classNames(
                anchorElement.className,
                linkStyles.linkWithSharingIssue
            );
            anchorElement.text = format(strings.expiredLinkText, anchorElement.text);
        }
    }

    const fileIcon: FileIcon = getIconForFile(fileName, mimeType === 'text/directory', mimeType);
    if (!fileIcon.cdn) {
        return;
    }

    const iconContainer: HTMLImageElement = document.createElement('img');
    iconContainer.src = fileIcon.cdn;
    iconContainer.style.width = '16px';
    iconContainer.style.height = '16px';
    iconContainer.style.verticalAlign = 'middle';
    iconContainer.style.paddingRight = '2px';
    anchorElement.insertAdjacentElement('afterbegin', iconContainer);
    anchorElement.style.whiteSpace = 'nowrap';
}

function highlightBackground(anchorElement: HTMLAnchorElement) {
    if (!isFeatureEnabled('doc-beautify-link')) {
        return;
    }

    anchorElement.style.backgroundColor = 'rgb(244,244,244)';
    anchorElement.style.borderRadius = '2px';
    anchorElement.style.padding = '0 2px 0 2px';
}

function displayErrorIcon(anchorElement: HTMLAnchorElement) {
    if (!isFeatureEnabled('doc-beautify-link')) {
        return;
    }

    const errorIconContainer: HTMLImageElement = document.createElement('img');
    errorIconContainer.src = `${getPackageBaseUrl()}resources/images/redinfo_24.png`;
    errorIconContainer.style.width = '12px';
    errorIconContainer.style.height = '12px';
    errorIconContainer.style.verticalAlign = 'top';
    anchorElement.appendChild(document.createComment(COMPOSE_ONLY_ATTACHMENT_CONTENT_START));
    anchorElement.appendChild(errorIconContainer);
    anchorElement.appendChild(document.createComment(COMPOSE_ONLY_ATTACHMENT_CONTENT_END));
    anchorElement.className = classNames(anchorElement.className, linkStyles.linkWithSharingIssue);
}

function areIdsFromSameLink(oldId: string, newId: string): boolean {
    return oldId === newId;
}
